import {
	useEffect,
	useState,
	useMemo,
	useCallback,
} from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { clearPublishedUrl, clearSelectedEvent, clearWorkingEvent } from '../../../store/actions/admin/create-event';
import { clearPrivacyPolicy } from '../../../store/actions/admin/privacy-policy';
import { clearTermsConditions } from '../../../store/actions/admin/terms-conditions';
import { clearWorkingSession } from '../../../store/actions/admin/create-event/session';
import { getAdmins } from '../../../store/actions/admin/bl-admin';
import { EventsState } from '../../../store/types';
import { BrandliveEvent, Session, SessionTypesEnum, SessionTypesFeatures } from '../../../types/working-model';
import Icon, { COLORS, ICONS } from '../../general-ui/icon';
import TabHeader from '../../general-ui/tab-header/tab-header';
import EventCard from './event-card';
import EventRow from './event-row';
import EventHeaderCard from './header-card';
import EventDetails from './event-details';
import Search from '../../general-ui/search-bar/search';
import SelectInput from '../../general-ui/select/select';
import IconToggle from '../../general-ui/icon-toggle/icon-toggle';
import AccessIcons from './access-icons';
import { Link } from 'react-router-dom';
import { PATHNAMES } from '../admin';
import { EventDetailsHeader } from './event-details-header';
import Paging from '../../general-ui/paging/paging';
import EventsEmpty from '../../../images/events-empty.png';
import WaitingIndicator from '../../general-ui/waiting-indicator/waiting-indicator';
import classNames from 'classnames';
import { GetChannelEvents } from '../../../connection/analytics';
import { getChannelDurations, getChannelViews } from '../../../store/actions/admin/analytics';
import { getStartTime } from '../../../utils/get-event-start-time';
import './events-main.scss';
import { Status } from '../../../types/event';
import { AuthWrapper } from '../authentication/AuthWrapper';
import { checkIsLive } from '../../../utils/utils';
import useTranslationModules from '../../../i18n/useTranslationModules';


interface BLEventWithRegistration extends BrandliveEvent {
	registrations: string;
}

const sortByOptions = [
	{
		label: 'Newest',
		value: 'newest',
	},
	{
		label: 'Oldest',
		value: 'oldest',
	},
	{
		label: 'A-Z',
		value: 'a-z',
	},
	{
		label: 'Z-A',
		value: 'z-a',
	},
	{
		label: 'Published',
		value: 'published'
	},
	{
		label: 'Unpublished',
		value: 'unpublished'
	},
	{
		label: 'Most Registrations',
		value: 'high-registration'
	},
	{
		label: 'Least Registrations',
		value: 'low-registration'
	},
];

const TABS = ['Live', 'Upcoming', 'Past', 'On Demand']; // order matters

export default function EventsMain(): JSX.Element {

	const events = useSelector((state: EventsState) => state.EventsReducer.events);
	const eventsLoading = useSelector((state: EventsState) => state.EventsReducer.eventsLoading);
	const eventsError = useSelector((state: EventsState) => state.EventsReducer.eventsError);
	const user = useSelector((state: EventsState) => state.AuthReducer.user);
	const token = useSelector((state: EventsState) => state.AuthReducer.token);
	const changingChannels = useSelector((state: EventsState) => state.AuthReducer.changingChannels);
	const selectedEvent = useSelector((state: EventsState) => state.CreateEventReducer.selectedEvent);
	const channelViews = useSelector((state: EventsState) => state.AnalyticsReducer.channelViews);
	const channelDuration = useSelector((state: EventsState) => state.AnalyticsReducer.channelDuration);

	const dispatch = useDispatch();

	useTranslationModules({ language: "en" });

	const [activeTab, setActiveTab] = useState(TABS[0]);
	const [sortBy, setSortBy] = useState('');
	const [currentEventList, setCurrentEventList] = useState<BrandliveEvent[]>(events);
	const [view, setView] = useState('grid');
	const [currentPage, setCurrentPage] = useState(1);
	const [maxPage, setMaxPage] = useState(1);
	const [eventRegistrations, setEventRegistrations] = useState<{ [key: number]: number }>({});
	const [liveEventMap, setLiveEventMap] = useState<{ [key: string]: Status }>({});
	const [initialized, setInitialized] = useState(false);

	const pageSize = 9;

	useEffect(() => {
		if (!changingChannels) {
			batch(() => {
				dispatch(clearSelectedEvent());
				dispatch(clearWorkingEvent());
				dispatch(clearPublishedUrl());
				dispatch(clearPrivacyPolicy());
				dispatch(clearTermsConditions());
				dispatch(clearWorkingSession());
				dispatch(getAdmins(token!));
				dispatch(getChannelViews(user!.active_channel, token!));
				dispatch(getChannelDurations(user!.active_channel, token!));
			});
		}
	}, [user, token, dispatch, changingChannels]);

	useEffect(() => {
		setCurrentEventList(events);
	}, [events]);

	function handleViewToggle() {
		if (view === 'grid') {
			setView('list');
		} else { setView('grid'); }
	}

	const submitEventSearch = (search: string) => {
		const array: BrandliveEvent[] = currentEventList.filter(
			(event: BrandliveEvent) =>
				event.name.toLowerCase().includes(search.toLowerCase())
		);
		setCurrentEventList(array);
	};

	const handleClear = () => {
		setCurrentEventList(events);
		setSortBy('');
	};

	const defaultSort = (eventList: BrandliveEvent[]): BrandliveEvent[] => {
		return eventList.sort((a, b) => {
			const dateA = getStartTime(a, false) || 0;
			const dateB = getStartTime(b, false) || 0;
			if (dateA > dateB) return -1;
			if (dateA < dateB) return 1;
			return 0;
		});
	};

	const liveEvents = useMemo(() => {
		const array = currentEventList.map((event: BrandliveEvent) => {
			return event.sessions.map((session: Session) => {
				if (checkIsLive(session)) {
					return event;
				}
			});
		}) ?? [];
		const formattedArray: BrandliveEvent[] = [];
		for (let i = 0; i < array.length; i++) {
			if (array[i]) {
				for (let j = 0; j < array[i].length; j++) {
					if (array[i][j]) {
						// populate liveEventMap for on-demand tab to display correct events that are live
						// use map so we dont need to recalculate anything again
						const brandliveEvent = array[i][j]!;
						setLiveEventMap(prevState => ({
							...prevState,
							[brandliveEvent.uuid]: brandliveEvent.status
						}));
						formattedArray.push(brandliveEvent);
						break;
					}
				}
			}
		}
		return defaultSort(formattedArray);
	}, [currentEventList]);

	const upcomingEvents = useMemo(() => {
		const date = Date.now();
		const array =
			currentEventList.map((event: BrandliveEvent) => {
				return event.sessions.map((session: Session) => {
					if (session.end_timestamp && SessionTypesFeatures[session.session_type].has_start_time) {
						const timestamp = new Date(session.timestamp!);
						if (timestamp.getTime() > date) {
							return event;
						}
					}
				});
			}) ?? [];
		const formattedArray: BrandliveEvent[] = [];
		for (let i = 0; i < array.length; i++) {
			for (let j = 0; j < array[i].length; j++) {
				if (array[i][j]) {
					formattedArray.push(array[i][j]!);
					break;
				}
			}
		}
		return defaultSort(formattedArray);
	}, [currentEventList]);

	const pastEvents = useMemo(() => {
		const date = Date.now();
		const array = currentEventList.map((event: BrandliveEvent) => {
			if (event) {
				let allPast = true;
				let allOnDemand = true;
				event.sessions.map((session: Session) => {
					if (session.end_timestamp && SessionTypesFeatures[session.session_type].has_start_time) {
						allOnDemand = false;
						const end_timestamp = new Date(
							session.end_timestamp!
						);
						if (end_timestamp.getTime() > date) { allPast = false; }
					}
				});
				if (allPast && !allOnDemand) { return event; }
			}
		}) ?? [];
		const formattedArray: BrandliveEvent[] = [];
		for (let i = 0; i < array.length; i++) {
			if (array[i]) {
				formattedArray.push(array[i]!);
			}
		}
		return defaultSort(formattedArray);
	}, [currentEventList]);

	const onDemandEvents = useMemo(() => {
		const array = currentEventList.map((event: BrandliveEvent) => {
			return event.sessions.map((session: Session) => {
				if (session?.session_type === SessionTypesEnum.onDemand) { return event; }
			});
		}) ?? [];
		const formattedArray: BrandliveEvent[] = [];
		for (let i = 0; i < array.length; i++) {
			for (let j = 0; j < array[i].length; j++) {
				if (array[i][j]) {
					formattedArray.push(array[i][j]!);
					break;
				}
			}
		}

		return defaultSort(formattedArray);
	}, [currentEventList]);

	const formattedEventList = useMemo(() => {
		switch (activeTab) {
			case TABS[0]: {
				setCurrentPage(1);
				setMaxPage(Math.ceil(liveEvents.length / pageSize));
				setSortBy('');
				return liveEvents;
			}
			case TABS[1]: {
				setCurrentPage(1);
				setMaxPage(Math.ceil(upcomingEvents.length / pageSize));
				setSortBy('');
				return upcomingEvents;
			}
			case TABS[2]: {
				setCurrentPage(1);
				setMaxPage(Math.ceil(pastEvents.length / pageSize));
				setSortBy('');
				return pastEvents;
			}
			case TABS[3]: {
				setCurrentPage(1);
				setMaxPage(Math.ceil(onDemandEvents.length / pageSize));
				setSortBy('');
				return onDemandEvents;
			}
			default:
				return [];
		}
	}, [activeTab, liveEvents, upcomingEvents, pastEvents, onDemandEvents, pageSize]);

	useEffect(() => {
		GetChannelEvents(user!.active_channel, token!)
			.then((allRegistration) => {
				const registrationsByEvent = allRegistration.reduce((a: { [key: number]: number }, b: BLEventWithRegistration) => {
					a[b.event!] = Number(b.registrations);
					return a;
				}, {});
				setEventRegistrations(registrationsByEvent);
			});
	}, [token, user]);

	useEffect(() => {
		// the initialized check is needed so that filtering doesn't make the tab jump back
		if (!eventsLoading && !initialized) {
			setInitialized(true);
			if (!liveEvents?.length) {
				setActiveTab(TABS[1]);
			} else {
				setActiveTab(TABS[0]);
			}
		}
	}, [liveEvents, eventsLoading, initialized]);

	const handleSortByChange = useCallback(
		(value: string) => {
			setSortBy(value);
			const finishedFetchingRegistration = Object.keys(eventRegistrations).length > 0;
			let currentlist = upcomingEvents;
			switch (activeTab) {
				case TABS[0]: {
					currentlist = liveEvents;
					break;
				}
				case TABS[1]: {
					break;
				}
				case TABS[2]: {
					currentlist = pastEvents;
					break;
				}
				case TABS[3]: {
					currentlist = onDemandEvents;
					break;
				}
				default:
					break;
			}

			switch (value) {
				case 'newest': {
					currentlist.sort((a, b) => {
						const dateA = getStartTime(a, false) || 0;
						const dateB = getStartTime(b, false) || 0;
						if (dateA > dateB) return -1;
						if (dateA < dateB) return 1;
						return 0;
					});
					break;
				}
				case 'oldest': {
					currentlist.sort((a, b) => {
						const dateA = getStartTime(a, false) || 0;
						const dateB = getStartTime(b, false) || 0;
						if (dateA < dateB) return -1;
						if (dateA > dateB) return 1;
						return 0;
					});
					break;
				}
				case 'a-z': {
					currentlist.sort((a, b) => {
						const nameA = a.name.toLowerCase();
						const nameB = b.name.toLowerCase();

						if (nameA < nameB) return -1;
						if (nameA > nameB) return 1;
						return 0;
					});
					break;
				}
				case 'z-a': {
					currentlist.sort((a, b) => {
						const nameA = a.name.toLowerCase();
						const nameB = b.name.toLowerCase();
						if (nameA > nameB) return -1;
						if (nameA < nameB) return 1;
						return 0;
					});
					break;
				}
				case 'high-registration': {
					currentlist.sort((a, b) => {
						// if we havent finished getting event registration numbers, 
						// keep the list the same and reset the sortBy
						if (finishedFetchingRegistration) {
							if (eventRegistrations[a.event!] > eventRegistrations[b.event!]) return -1;
							if (eventRegistrations[a.event!] < eventRegistrations[b.event!]) return 1;
							return 0;
						} else {
							setSortBy('');
							return 0;
						}
					});
					break;
				}
				case 'low-registration': {
					currentlist.sort((a, b) => {
						if (finishedFetchingRegistration) {
							if (eventRegistrations[a.event!] < eventRegistrations[b.event!]) return -1;
							if (eventRegistrations[a.event!] > eventRegistrations[b.event!]) return 1;
							return 0;
						} else {
							setSortBy('');
							return 0;
						}
					});
					break;
				}
				case 'published': {
					currentlist.sort((a, b) => {
						if (a.status < b.status) return -1;
						if (a.status > b.status) return 1;
						return 0;
					});
					break;
				}
				case 'unpublished': {
					currentlist.sort((a, b) => {
						if (a.status > b.status) return -1;
						if (a.status < b.status) return 1;
						return 0;
					});
					break;
				}
				default:
					return;
			}
		},
		[activeTab, eventRegistrations, liveEvents, onDemandEvents, pastEvents, upcomingEvents]
	);

	const showEmpty = formattedEventList && formattedEventList.length === 0;

	const handleSortList = (value: 'newest' | 'oldest' | 'a-z' | 'z-a' | 'high-registration' | 'low-registration' | 'published' | 'unpublished') => {
		return () => handleSortByChange(value);
	};

	const checkEmptySearch = (search: string) => {
		// when search bar has search string deleted instead of clearing via escape key, perform clear
		if (search === '') {
			handleClear();
		}
	};

	const handleTabChange = (value: string) => {
		setActiveTab(value);
	};

	return (
		<main className="events-main wrapper">
			{selectedEvent ? <EventDetailsHeader /> : <></>}
			<div className="events-container">
				{selectedEvent ? (
					<EventDetails />
				) : (
					<>
						<div className="events-title">
							<h2>Events</h2>
							<AuthWrapper>
								<Link
									className="create-event"
									to={PATHNAMES.CreateEvent}
									onClick={() => dispatch(clearPublishedUrl())}
								>
									+ Create Event
								</Link>
							</AuthWrapper>
						</div>
						<div className="events-header-container">
							<EventHeaderCard
								metricTitle={'Total Events'}
								metricValue={events.length}
								info='The number of events that have been planned or have occurred'
							/>
							<EventHeaderCard
								metricTitle={'Total views'}
								metricValue={Number(channelViews.data.value)}
								info='The total number of page views'
							/>
							<EventHeaderCard
								metricTitle={'Avg. duration, min'}
								metricValue={Number(channelDuration.data.value)}
								info='The average connection time each page was viewed'
							/>
							<div className="events-header-card">
								<a href="https://help.brand.live/hc/en-us" target="_blank" rel="noreferrer">
									<div className="header-link">
										Learn more
										<Icon
											name={ICONS.NEW_WINDOW}
											size={16}
											color={COLORS.DEFAULT_GRAY}
										/>
									</div>
								</a>
							</div>
						</div>
						<div className="tab-wrapper">
							<div className="tab-container">
								<div className="tab-header">
									{/* adding this conditional prevents staggering tabs */}
									{eventsLoading ? null : (
										<TabHeader
											tabs={!liveEvents?.length ? [...TABS.slice(0, 0), ...TABS.slice(0 + 1)] : TABS}
											append={!liveEvents?.length ? [
												upcomingEvents.length,
												pastEvents.length,
												onDemandEvents.length,
											] :
												[
													liveEvents.length,
													upcomingEvents.length,
													pastEvents.length,
													onDemandEvents.length,
												]}
											onClick={handleTabChange}
											currentTab={activeTab}
										/>
									)}
								</div>
							</div>
							<div className="search-container">
								<AccessIcons addAccess={true} />
								<div>
									<Search
										onSearch={submitEventSearch}
										onClear={handleClear}
										onChange={checkEmptySearch}
									/>
								</div>
								<div className="sort-container">
									<SelectInput
										placeholder="Sort by"
										mainIcon={
											<Icon
												name={ICONS.KEYBOARD_ARROW_DOWN}
												size={14}
												color="white"
											/>
										}
										options={sortByOptions}
										selected={sortBy}
										small
										noMaxHeight
										onChange={handleSortByChange}
									/>
								</div>
								<div>
									<IconToggle
										name="view toggle"
										leftValue="list"
										leftIconName={ICONS.LAYOUT_LIST}
										rightValue="grid"
										rightIconName={ICONS.LAYOUT_GRID}
										onToggle={handleViewToggle}
										selected={view}
									/>
								</div>
							</div>
						</div>

						{eventsLoading ? (
							<h3></h3>
						) : (
							<>
								{eventsError ? (
									<h4>Error loading events</h4>
								) : (
									<>
										{view === 'grid' ? (
											<div className="events-grid-body">
												{showEmpty ? (
													<div className="events-empty-container">
														<img src={EventsEmpty} />

														<h3>No events are currently live</h3>
													</div>
												) : (
													<>
														{formattedEventList?.map((event: BrandliveEvent, index: number) => {
															if ((index >= ((currentPage - 1) * pageSize)) && (index < (currentPage * pageSize))) {
																return (
																	<div
																		className="event-container"
																		key={event.uuid}
																	>
																		<EventCard
																			event={event}
																			eventRegistrations={eventRegistrations[event.event!]}
																			isLive={!!liveEventMap[event.uuid]}
																		/>
																	</div>
																);
															}
														})}
													</>
												)}

											</div>
										) : (
											<>
												<div className="events-list-body">
													<ul>
														<li className="list-header">
															<div className="header">
																<span>
																	EVENT NAME
																</span>
																<span className="header-arrows" onClick={handleSortList(sortBy === 'a-z' ? 'z-a' : 'a-z')}>
																	<Icon
																		name={
																			ICONS.LITTLE_ARROW_UP
																		}
																		size={5}
																		color={
																			COLORS.DEFAULT_GRAY
																		}
																	/>
																	<Icon
																		name={
																			ICONS.LITTLE_ARROW_DOWN
																		}
																		size={5}
																		color={
																			COLORS.DEFAULT_GRAY
																		}
																	/>
																</span>
															</div>
															<div className="header">
																<span>
																	DATE
																</span>
																<span className="header-arrows" onClick={handleSortList(sortBy === 'newest' ? 'oldest' : 'newest')}>
																	<Icon
																		name={
																			ICONS.LITTLE_ARROW_UP
																		}
																		size={5}
																		color={
																			COLORS.DEFAULT_GRAY
																		}
																	/>
																	<Icon
																		name={
																			ICONS.LITTLE_ARROW_DOWN
																		}
																		size={5}
																		color={
																			COLORS.DEFAULT_GRAY
																		}
																	/>
																</span>
															</div>
															<div className="header">
																<span>
																	REGISTRATIONS
																</span>
																<span className="header-arrows" onClick={handleSortList(sortBy === 'high-registration' ? 'low-registration' : 'high-registration')}>
																	<Icon
																		name={
																			ICONS.LITTLE_ARROW_UP
																		}
																		size={5}
																		color={
																			COLORS.DEFAULT_GRAY
																		}
																	/>
																	<Icon
																		name={
																			ICONS.LITTLE_ARROW_DOWN
																		}
																		size={5}
																		color={
																			COLORS.DEFAULT_GRAY
																		}
																	/>
																</span>
															</div>
															<div className="header">
																<span>
																	STATUS
																</span>
																<span className="header-arrows" onClick={handleSortList(sortBy === 'published' ? 'unpublished' : 'published')}>
																	<Icon
																		name={
																			ICONS.LITTLE_ARROW_UP
																		}
																		size={5}
																		color={
																			COLORS.DEFAULT_GRAY
																		}
																	/>
																	<Icon
																		name={
																			ICONS.LITTLE_ARROW_DOWN
																		}
																		size={5}
																		color={
																			COLORS.DEFAULT_GRAY
																		}
																	/>
																</span>
															</div>
														</li>
														{formattedEventList?.map(
															(
																event: BrandliveEvent,
																index: number
															) => {
																if ((index >= ((currentPage - 1) * pageSize)) && (index < (currentPage * pageSize))) {
																	return (
																		<li
																			key={
																				event.uuid
																			}
																			className="list-item"
																		>
																			<EventRow
																				event={
																					event
																				}
																				eventRegistrations={eventRegistrations[event.event!]}
																			/>
																		</li>
																	);
																}
															}
														)}
													</ul>
												</div>
											</>
										)}
										{maxPage > 1 ? <Paging page={currentPage} setPage={setCurrentPage} totalPages={maxPage} /> : <></>}
									</>
								)}
							</>
						)}
					</>
				)}
			</div>
			<div className={classNames("events-loading", { active: eventsLoading })}>
				<WaitingIndicator />
			</div>
		</main>
	);
}
